import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard" },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/LoginView.vue"),
      meta: { public: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("@/views/ForgotPasswordView.vue"),
      meta: { public: true },
    },
    {
      path: "/reset-password/:token",
      name: "reset-password",
      component: () => import("@/views/ResetPasswordView.vue"),
      meta: { public: true },
    },
    {
      // Authentikin redirect URI. Julkinen: käyttäjällä EI vielä ole
      // istuntoa kun Authentik ohjaa tänne — tämä näkymä on se joka
      // hankkii sen. Polku on sovittu Authentikin providerissa
      // (ks. docs/AUTHENTIK_AUTH.md), älä nimeä sitä uudelleen
      // muuttamatta redirect URI -listaa samalla.
      path: "/auth/callback",
      name: "oidc-callback",
      component: () => import("@/views/OidcCallbackView.vue"),
      meta: { public: true },
    },
    {
      path: "/change-password",
      name: "change-password",
      component: () => import("@/views/ChangePasswordView.vue"),
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: () => import("@/views/DashboardView.vue"),
    },
    {
      path: "/customers",
      name: "customers",
      component: () => import("@/views/CustomersView.vue"),
    },
    {
      path: "/customers/:id",
      name: "customer-detail",
      component: () => import("@/views/CustomerDetailView.vue"),
    },
    {
      path: "/contracts",
      name: "contracts",
      component: () => import("@/views/ContractsView.vue"),
    },
    {
      path: "/contracts/:id",
      name: "contract-detail",
      component: () => import("@/views/ContractDetailView.vue"),
    },
    {
      // Asiakasportaalista tulleiden palvelupyyntöjen työjono. Oma
      // päätason reitti sopimusten rinnalla: pyyntö on löydyttävä
      // ilman että tietää etukäteen mitä sopimusta se koskee.
      path: "/service-requests",
      name: "service-requests",
      component: () => import("@/views/ServiceRequestsView.vue"),
      meta: { requirePermission: "service_request:read" },
    },
    {
      path: "/reports",
      name: "reports",
      component: () => import("@/views/ReportsView.vue"),
    },
    {
      path: "/audit-logs",
      name: "audit-logs",
      component: () => import("@/views/AuditLogsView.vue"),
      meta: { requirePermission: "audit_log:read" },
    },
    {
      path: "/notifications",
      name: "notifications",
      component: () => import("@/views/NotificationsView.vue"),
    },
    {
      path: "/email-templates",
      name: "email-templates",
      component: () => import("@/views/EmailTemplatesView.vue"),
      meta: { requirePermission: "email_template:read" },
    },
    {
      path: "/email-campaigns",
      name: "email-campaigns",
      component: () => import("@/views/EmailCampaignsView.vue"),
      meta: { requirePermission: "email_campaign:read" },
    },
    {
      path: "/email-campaigns/:id",
      name: "email-campaign-detail",
      component: () => import("@/views/EmailCampaignDetailView.vue"),
      meta: { requirePermission: "email_campaign:read" },
    },
    {
      path: "/brands",
      name: "brands",
      component: () => import("@/views/BrandsView.vue"),
      meta: { requirePermission: "brand:read" },
    },
    {
      path: "/brands/:brandId/customers",
      name: "brand-customers",
      component: () => import("@/views/BrandCustomersView.vue"),
      meta: { requirePermission: "customer_brand:read" },
    },
    {
      path: "/hmac-clients",
      name: "hmac-clients",
      component: () => import("@/views/HmacClientsView.vue"),
      // The shared HmacClient view covers both panel and MCP rows;
      // the API gates each verb on the matching panel_api_key:* /
      // mcp_client:* permission. We require panel_api_key:read for
      // the route — admins always have both, and the few non-admin
      // roles that can manage one kind today (none, in MVP) would
      // need the matching read permission anyway.
      meta: { requirePermission: "panel_api_key:read" },
    },
    {
      path: "/users",
      name: "users",
      component: () => import("@/views/UsersView.vue"),
      meta: { requirePermission: "user:read" },
    },
    {
      path: "/roles",
      name: "roles",
      component: () => import("@/views/RolesView.vue"),
      meta: { requirePermission: "role:read" },
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import("@/views/SettingsView.vue"),
      meta: { requirePermission: "system_settings:read" },
    },
    {
      path: "/imports/hostbill",
      name: "imports-hostbill",
      component: () => import("@/views/HostbillImportView.vue"),
      meta: { requirePermission: "import:read" },
    },
    {
      path: "/products",
      name: "products",
      component: () => import("@/views/ProductsView.vue"),
      meta: { requirePermission: "product:read" },
    },
    {
      path: "/product-families",
      name: "product-families",
      component: () => import("@/views/ProductFamiliesView.vue"),
      meta: { requirePermission: "product_family:read" },
    },
    {
      path: "/product-templates",
      name: "product-templates",
      component: () => import("@/views/ProductTemplatesView.vue"),
      meta: { requirePermission: "product-template:read" },
    },
    {
      path: "/product-categories",
      name: "product-categories",
      component: () => import("@/views/ProductCategoriesView.vue"),
      meta: { requirePermission: "product-category:read" },
    },
    // Catch-all → redirect to dashboard. Keeps SPA fallback simple
    // while the route table is small.
    { path: "/:pathMatch(.*)*", redirect: "/dashboard" },
  ],
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta.public) return true;
  if (!auth.isAuthenticated) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }
  // Permission-gated routes — redirect away rather than render a 403,
  // since the sidebar already hides links the user can't reach. We
  // still re-check here to prevent direct-URL access bypassing the
  // RBAC. The auth store loads `permissions` lazily via fetchMe(), so
  // when `user` isn't ready yet we let the request through and rely
  // on the API's 403 for the safety net.
  const required = to.meta.requirePermission as string | undefined;
  if (required && auth.user && !auth.user.permissions.includes(required)) {
    return { path: "/dashboard" };
  }
  return true;
});

/**
 * Auto-recover from "stale entry bundle" deploys: a long-lived
 * browser tab keeps the previous build's entry JS in memory, which
 * still references the previous build's lazy-loaded view chunks.
 * After a redeploy those chunk hashes are gone and Vue Router's
 * dynamic `import()` throws a `ChunkLoadError` / "Failed to fetch
 * dynamically imported module" when the user clicks a route that
 * needs one.
 *
 * Catching it here and forcing a single `window.location.reload()`
 * picks up the new index.html (no-cache) + fresh entry JS, and
 * the navigation that originally failed completes on the new
 * bundle. Guarded against an infinite reload loop by recording
 * the attempt in sessionStorage and bailing out if the same
 * route fails a second time in quick succession.
 */
const RELOAD_KEY = "web1-crm.router.lastChunkErrorReload";
router.onError((err: unknown, to) => {
  const msg = (err as Error).message ?? String(err);
  const looksLikeChunkLoadFailure =
    /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|Importing a module script failed/i.test(
      msg,
    );
  if (!looksLikeChunkLoadFailure) return;

  try {
    const stamp = `${to.fullPath}@${Date.now()}`;
    const prev = sessionStorage.getItem(RELOAD_KEY) ?? "";
    const [prevPath, prevTsRaw] = prev.split("@");
    const prevTs = prevTsRaw ? Number.parseInt(prevTsRaw, 10) : 0;
    if (prevPath === to.fullPath && Date.now() - prevTs < 10_000) {
      // Already tried within 10 s; bail rather than loop.
      return;
    }
    sessionStorage.setItem(RELOAD_KEY, stamp);
  } catch {
    // sessionStorage may throw in privacy modes — fall through to
    // reload anyway; an extra reload is better than a stuck app.
  }
  window.location.reload();
});
